<script setup lang="ts">
import {
  Check,
  ChevronLeft,
  EyeOff,
  LayoutPanelLeft,
  Loader2,
  LogOut,
  Palette,
  Save,
  Settings,
  Share2,
  Smartphone,
  TriangleAlert
} from 'lucide-vue-next'
import useWebappTabs from './composables/webappPageTabs'
import useWebappEditorData from './composables/useWebappEditorData'
import useContentEditing from './composables/useContentEditing'
import useColorPreview from './composables/useColorPreview'
import SettingsTab from './components/SettingsTab.vue'
import ShareCard from './components/ShareCard.vue'
import TestOnPhonePopover from './components/TestOnPhonePopover.vue'
import PreviewTab from './components/PreviewTab.vue'
import DesignPanel from './components/DesignPanel.vue'
import SectionPanel from './components/SectionPanel.vue'
import NavigationPanel from './components/sections/NavigationPanel.vue'
import HomePanel from './components/sections/HomePanel.vue'
import LoyaltyLayoutPanel from './components/sections/LoyaltyLayoutPanel.vue'
import SectionList from './components/SectionList.vue'
import ScreenIllustration from './components/ScreenIllustration.vue'
import EditorModeRail from './components/EditorModeRail.vue'
import MobileEditorNotice from './components/MobileEditorNotice.vue'
import PreviewOnlyBadge from './components/PreviewOnlyBadge.vue'
import RegistrationStepList from './components/sections/loyalty/RegistrationStepList.vue'
import OrderChannelList from './components/sections/order/OrderChannelList.vue'
import {
  HERO_SECTION,
  HOME_BLOCK_SECTIONS,
  HOME_LAYOUT_SECTION,
  LOYALTY_LAYOUT_SECTION,
  NAVIGATION_SECTION,
  DESIGN_SECTIONS,
  SCREEN_SECTIONS,
  SETTINGS_SECTIONS,
  STORES_SCREEN_SECTIONS,
  REGISTRATION_SECTION,
  AUDIENCE_RULES_SECTION,
  findSection,
  hasTextTab,
  textTabLabel,
  loyaltyScreenSections,
  parentSection,
  previewZoneForSection,
  isPreviewOnlySection,
  COMING_SOON_SCREENS
} from './lib/brandAppSections'
import type { BrandAppSection, PanelTab } from './lib/brandAppSections'
import {
  BRAND_APP_TABS,
  COMING_SOON_HOME_BLOCKS,
  isComingSoonHomeBlock,
  isHiddenHomeBlock,
  visibleHomeBlocks,
  type BrandAppTabId,
  type HomeBlockId
} from './lib/brandAppContract'
import { orderedSubset } from './lib/orderedSubset'
import {
  isSameEditorLocation,
  onlyTabChanged,
  parseEditorQuery,
  withEditorQuery,
  type EditorLocation,
  type EditorMode
} from './lib/editorQuery'

const { t } = useI18n()
const { syncPreviewTab, previewZone } = useWebappTabs()
const {
  audiencePreviewRule,
  audiences,
  customerStatus,
  brandAppConfig,
  cancelLeave,
  confirmLeave,
  deletedImages,
  firstStoreName,
  hasInvalidUrls,
  isDirty,
  isLoading,
  isSaving,
  isUnsavedChangesModalOpen,
  leaveEditor,
  loyaltyProgram,
  loyaltyStores,
  merchantConfigs,
  merchantTexts,
  merchantTextsForm,
  renderImages,
  rewards,
  submitWebapp,
  user,
  webapp,
  webappImages,
  webappURL,
  welcomeTexts
} = useWebappEditorData()

const MODES = computed(() => [
  { id: 'screen' as const, name: t('pages.loyaltySupports.webapp.editor.modes.screen'), icon: Smartphone },
  { id: 'theme' as const, name: t('pages.loyaltySupports.webapp.editor.modes.theme'), icon: Palette },
  { id: 'structure' as const, name: t('pages.loyaltySupports.webapp.editor.modes.structure'), icon: LayoutPanelLeft },
  { id: 'share' as const, name: t('pages.loyaltySupports.webapp.editor.modes.share'), icon: Share2 },
  { id: 'settings' as const, name: t('pages.loyaltySupports.webapp.editor.modes.settings'), icon: Settings }
])

type PanelAnim = 'forward' | 'back' | 'switch'

const PANEL_ENTER_CLASS: Record<PanelAnim, string> = {
  forward: 'motion-safe:animate-panel-forward',
  back: 'motion-safe:animate-panel-back',
  switch: 'motion-safe:animate-panel-switch'
}

const mode = ref<EditorMode>('screen')
const panelAnim = ref<PanelAnim>('switch')
const openedSection = ref<string | null>(null)
const blockTab = ref<PanelTab>('content')
const { isColorPreviewed } = useColorPreview()

const isComingSoon = (tab: BrandAppTabId) => COMING_SOON_SCREENS.includes(tab)

const activeAppTab = ref<BrandAppTabId>(
  isComingSoon(brandAppConfig.defaultTab.value) ? 'loyalty' : brandAppConfig.defaultTab.value
)

const panelRef = useTemplateRef<HTMLElement>('panelRef')

const justSaved = ref(false)
let justSavedTimer: ReturnType<typeof setTimeout> | undefined

const save = async () => {
  await submitWebapp()
  if (isDirty.value) return

  justSaved.value = true
  clearTimeout(justSavedTimer)
  justSavedTimer = setTimeout(() => (justSaved.value = false), 3000)
}

onBeforeUnmount(() => clearTimeout(justSavedTimer))

const scrollPanelTop = () => nextTick(() => panelRef.value?.scrollTo({ top: 0 }))

const screenName = (tab: BrandAppTabId) => t(`pages.loyaltySupports.webapp.editor.screens.${tab}`)

const screenTabs = computed(() =>
  orderedSubset(
    BRAND_APP_TABS.map((tab) => tab.id),
    brandAppConfig.tabs.value,
    brandAppConfig.tabOrder.value
  )
    .map((id) => BRAND_APP_TABS.find((tab) => tab.id === id))
    .filter((tab) => tab !== undefined)
)

const isScreenDisabled = (id: BrandAppTabId) => !isComingSoon(id) && !brandAppConfig.tabs.value.includes(id)

const firstScreen = computed<BrandAppTabId>(
  () => screenTabs.value.find((tab) => !isComingSoon(tab.id) && !isScreenDisabled(tab.id))?.id ?? 'loyalty'
)

const screenHint = (id: BrandAppTabId) => {
  if (isComingSoon(id)) return t('pages.loyaltySupports.webapp.editor.screens.comingSoon')
  if (isScreenDisabled(id)) return t('pages.loyaltySupports.webapp.editor.screens.hiddenHint')

  return null
}

const showScreen = (id: BrandAppTabId) => brandAppConfig.setTabs([...brandAppConfig.tabs.value, id])

const screenSections = computed<BrandAppSection[]>(() => {
  if (activeAppTab.value === 'loyalty') return loyaltyScreenSections(brandAppConfig.hasLoyalty.value)
  if (activeAppTab.value === 'stores') return STORES_SCREEN_SECTIONS
  if (activeAppTab.value === 'home') {
    const blocks = orderedSubset(
      visibleHomeBlocks().map((block) => block.id),
      brandAppConfig.home.value.blocks,
      brandAppConfig.homeBlockOrder.value
    )

    return [HERO_SECTION, ...blocks.map((id) => HOME_BLOCK_SECTIONS[id]).filter(Boolean)]
  }

  return [SCREEN_SECTIONS[activeAppTab.value]].filter(Boolean)
})

const toggleableHomeBlocks = computed(() =>
  activeAppTab.value === 'home'
    ? visibleHomeBlocks()
        .map((block) => block.id)
        .filter((id) => !isComingSoonHomeBlock(id))
    : []
)

const setHomeBlockEnabled = (id: string, enabled: boolean) => {
  const order = orderedSubset(
    visibleHomeBlocks().map((block) => block.id),
    brandAppConfig.home.value.blocks,
    brandAppConfig.homeBlockOrder.value
  )
  brandAppConfig.setHomeBlockOrder(order)
  brandAppConfig.setHomeBlocks(
    order.filter((block) => (block === id ? enabled : brandAppConfig.home.value.blocks.includes(block)))
  )
}

const activeScreenSection = computed(() => {
  if (openedSection.value) return openedSection.value

  return screenSections.value.length === 1 ? screenSections.value[0].id : null
})

const syncPreviewToSection = (id: string | null) => {
  const zone = id ? previewZoneForSection(id) : undefined
  if (zone) previewZone.value = zone
  else syncPreviewTab(activeAppTab.value)
}

const openedSectionDef = computed(() => (openedSection.value ? findSection(openedSection.value) : undefined))

const isListView = computed(() => {
  if (mode.value === 'share') return false
  if (mode.value === 'screen') return !activeScreenSection.value

  return !openedSection.value
})

const panelKey = computed(
  () => `${mode.value}:${openedSection.value ?? ''}:${mode.value === 'screen' ? activeAppTab.value : ''}`
)

const { draft: contentDraft, editing: contentEditing, stopEditing, view: contentView } = useContentEditing()

const previewStatusName = computed(() => {
  if (!customerStatus.value.isEnabled) return undefined
  const audienceId = customerStatus.value.audienceIds[0]

  return audiences.value.find((audience) => audience.id === audienceId)?.name
})

watch(activeAppTab, () => syncPreviewToSection(openedSection.value), { immediate: true })

watch(openedSection, (section) => {
  if (section !== AUDIENCE_RULES_SECTION.id) audiencePreviewRule.value = null
})

watch(panelKey, () => stopEditing())

const panelTitle = computed(() => {
  if (!contentEditing.value) return openedSectionDef.value?.name
  const { kind, isNew } = contentEditing.value
  if (kind === 'mission') {
    return t(`pages.loyaltySupports.webapp.editor.panel.${isNew ? 'newMission' : 'editMission'}`)
  }

  return t(`pages.loyaltySupports.webapp.${kind === 'offer' ? 'offers' : 'news'}.${isNew ? 'add' : 'edit'}`)
})

const goBack = () => {
  if (!contentEditing.value) return closeSection()
  panelAnim.value = 'back'
  stopEditing()
  scrollPanelTop()
}

const panelEnterClass = computed(() => (isListView.value ? 'animate-none' : PANEL_ENTER_CLASS[panelAnim.value]))

const onPreviewBlockClicked = (block: HomeBlockId | 'hero') => {
  if (mode.value !== 'screen') selectMode('screen')
  activeAppTab.value = 'home'
  syncPreviewTab('home')
  openSection(block)
}

const onPreviewTabClicked = (tab: BrandAppTabId) => {
  syncPreviewTab(tab)
  if (tab === activeAppTab.value) return
  panelAnim.value = 'switch'
  activeAppTab.value = tab
  if (mode.value === 'screen') {
    openedSection.value = null
    blockTab.value = 'content'
    scrollPanelTop()
  }
}

const selectScreen = (value: unknown) => {
  if (typeof value !== 'string' || isComingSoon(value as BrandAppTabId)) return
  panelAnim.value = 'switch'
  activeAppTab.value = value as BrandAppTabId
  openedSection.value = null
  blockTab.value = 'content'
  scrollPanelTop()
}

const openSection = (id: string) => {
  panelAnim.value = 'forward'
  openedSection.value = id
  blockTab.value = 'content'
  if (mode.value === 'screen' || mode.value === 'settings') syncPreviewToSection(id)
  scrollPanelTop()
}

const closeSection = () => {
  panelAnim.value = 'back'
  openedSection.value = openedSection.value ? parentSection(openedSection.value) : null
  blockTab.value = 'content'
  if (mode.value === 'screen' || mode.value === 'settings') syncPreviewToSection(openedSection.value)
  scrollPanelTop()
}

const registrationTexts = computed(() =>
  Object.fromEntries(
    Object.entries(merchantTextsForm.value.webappRegistrationTextsFields).map(([key, field]) => [
      key,
      field.value?.trim() === field.defaultValue ? '' : (field.value ?? '')
    ])
  )
)

const selectMode = (value: EditorMode) => {
  panelAnim.value = 'switch'
  mode.value = value
  if (value === 'screen') activeAppTab.value = firstScreen.value
  openedSection.value = null
  syncPreviewToSection(null)
  scrollPanelTop()
}

const route = useRoute()
const router = useRouter()
const initialAppTab = activeAppTab.value

const editorLocation = computed<EditorLocation>(() => ({
  mode: mode.value,
  screen: activeAppTab.value,
  section: openedSection.value,
  tab: blockTab.value
}))

const queryLocation = () =>
  parseEditorQuery(
    route.query,
    { ...editorLocation.value, screen: initialAppTab },
    {
      screen: (id) => BRAND_APP_TABS.some((tab) => tab.id === id) && !isComingSoon(id as BrandAppTabId),
      section: (id) => findSection(id) !== undefined && !isHiddenHomeBlock(id)
    }
  )

watch(editorLocation, (location, previous) => {
  if (isSameEditorLocation(location, queryLocation())) return
  const navigate = onlyTabChanged(previous, location) ? router.replace : router.push
  navigate({ query: withEditorQuery(route.query, location) })
})

watch(isLoading, (loading) => {
  if (!loading && route.query.screen === undefined) activeAppTab.value = firstScreen.value
})

watch(
  () => route.query,
  () => {
    const location = queryLocation()
    if (isSameEditorLocation(location, editorLocation.value)) return
    panelAnim.value = 'switch'
    mode.value = location.mode
    activeAppTab.value = location.screen
    openedSection.value = location.section
    blockTab.value = location.tab
    syncPreviewToSection(location.mode === 'screen' || location.mode === 'settings' ? location.section : null)
    scrollPanelTop()
  },
  { immediate: true }
)
</script>

<template>
  <div v-if="!isLoading" class="bg-app-bg absolute inset-0 flex flex-col overflow-hidden">
    <MobileEditorNotice class="md:hidden" @leave="leaveEditor" />
    <header class="flex h-16 shrink-0 items-center gap-3 px-4">
      <div class="flex w-21 shrink-0 justify-center">
        <AppTooltip :text="t('pages.loyaltySupports.webapp.editor.header.back')" tooltip-position="right">
          <Button
            variant="outline"
            size="icon-lg"
            :aria-label="t('pages.loyaltySupports.webapp.editor.header.back')"
            @click="leaveEditor"
          >
            <LogOut class="rotate-180" />
          </Button>
        </AppTooltip>
      </div>
      <div class="ml-auto flex items-center gap-3">
        <span
          v-if="isDirty"
          class="flex h-10 items-center gap-2 rounded-full border px-3.5 text-sm font-medium border-amber-200 bg-amber-50 text-amber-800"
        >
          <Save class="size-4.5" />
          {{ t('pages.loyaltySupports.webapp.editor.header.unsaved') }}
        </span>
        <span
          v-else-if="justSaved"
          class="flex h-10 items-center gap-2 rounded-full border px-3.5 text-sm font-medium border-border bg-background shadow-control text-muted-foreground"
        >
          <Check class="size-4.5" />
          {{ t('pages.loyaltySupports.webapp.editor.header.saved') }}
        </span>
        <TestOnPhonePopover
          :app-url="user.merchant.webAppUrl ?? ''"
          :logo="brandAppConfig.identity.value.logo"
          :has-unsaved-changes="isDirty"
        >
          <Button variant="outline">
            <Smartphone />
            {{ t('pages.loyaltySupports.webapp.editor.header.test') }}
          </Button>
        </TestOnPhonePopover>
        <Button variant="default-primary" :disabled="isSaving || !isDirty || hasInvalidUrls" @click="save">
          <Loader2 v-if="isSaving" class="animate-spin" />
          {{ t('pages.loyaltySupports.webapp.buttons.save') }}
        </Button>
      </div>
    </header>

    <div class="flex min-h-0 flex-1 items-stretch gap-4 p-4">
      <EditorModeRail :items="MODES" :model-value="mode" @update:model-value="selectMode" />

      <div
        class="border-border bg-background flex max-w-170 min-w-105 flex-[5] flex-col overflow-hidden rounded-2xl border shadow-surface"
      >
        <div v-if="openedSection" class="border-border flex shrink-0 items-center gap-3 border-b px-4 py-3">
          <Button
            variant="outline"
            size="icon-sm"
            :aria-label="t('pages.loyaltySupports.webapp.editor.panel.back')"
            @click="goBack"
          >
            <ChevronLeft />
          </Button>
          <span class="text-foreground min-w-0 truncate text-base leading-tight font-semibold">
            {{ panelTitle }}
          </span>
          <PreviewOnlyBadge v-if="!contentEditing && isPreviewOnlySection(openedSection)" class="ml-auto" />
        </div>

        <div ref="panelRef" class="min-h-0 flex-1 overflow-y-auto py-6">
          <Transition
            mode="out-in"
            :enter-active-class="panelEnterClass"
            leave-active-class="motion-safe:animate-panel-out"
          >
            <div :key="panelKey">
              <template v-if="mode === 'theme'">
                <DesignPanel v-if="openedSection" :section="openedSection" />
                <SectionList v-else :sections="DESIGN_SECTIONS" @open="openSection" />
              </template>

              <ShareCard
                v-else-if="mode === 'share'"
                :share-link="user.merchant.webAppUrl"
                :logo="brandAppConfig.identity.value.logo"
                :brand-name="user.merchant.name"
              />

              <template v-else-if="mode === 'settings'">
                <SettingsTab v-if="openedSection" v-model:loyalty-program="loyaltyProgram" :section="openedSection" />
                <SectionList v-else :sections="SETTINGS_SECTIONS" @open="openSection" />
              </template>

              <template v-else-if="mode === 'structure'">
                <SectionPanel
                  v-if="openedSection"
                  v-model:images="webappImages"
                  v-model:deleted-images="deletedImages"
                  :section="openedSection"
                  :loyalty-stores="loyaltyStores"
                  :initial-images="webapp.images"
                />
                <template v-else>
                  <div class="mb-6 flex flex-col gap-1 px-6">
                    <h2 class="text-foreground text-sm font-semibold">
                      {{ t('pages.loyaltySupports.webapp.editor.panel.structureTitle') }}
                    </h2>
                    <p class="text-foreground-secondary text-sm leading-relaxed">
                      {{ t('pages.loyaltySupports.webapp.editor.panel.structureIntro') }}
                    </p>
                  </div>
                  <NavigationPanel :title="NAVIGATION_SECTION.name" />
                  <HomePanel :title="HOME_LAYOUT_SECTION.name" />
                  <LoyaltyLayoutPanel :title="LOYALTY_LAYOUT_SECTION.name" />
                </template>
              </template>

              <template v-else>
                <div v-if="!openedSection" class="mb-6 flex gap-2 px-6">
                  <AppTooltip v-for="tab in screenTabs" :key="tab.id" :text="screenHint(tab.id)">
                    <Button
                      :variant="tab.id === activeAppTab ? 'outline-primary' : 'outline'"
                      :aria-pressed="tab.id === activeAppTab"
                      :aria-disabled="isComingSoon(tab.id) || undefined"
                      class="group relative h-auto min-w-0 flex-1 flex-col gap-1 px-1 pt-2 pb-2.5 text-xs leading-tight font-medium"
                      :class="{ 'cursor-default opacity-50': isComingSoon(tab.id) }"
                      @click="selectScreen(tab.id)"
                    >
                      <EyeOff
                        v-if="isScreenDisabled(tab.id)"
                        :stroke-width="2.5"
                        class="text-muted-foreground absolute top-1.5 right-1.5 size-3"
                        :aria-label="t('pages.loyaltySupports.webapp.editor.screens.hiddenHint')"
                      />
                      <ScreenIllustration
                        :screen="tab.id"
                        :active="tab.id === activeAppTab"
                        class="size-12"
                        :class="{ 'opacity-50': isScreenDisabled(tab.id) }"
                      />
                      <span
                        class="max-w-full min-w-0 truncate"
                        :class="{ 'text-muted-foreground': isComingSoon(tab.id) || isScreenDisabled(tab.id) }"
                        >{{ screenName(tab.id) }}</span
                      >
                    </Button>
                  </AppTooltip>
                </div>

                <div v-if="!openedSection && isScreenDisabled(activeAppTab)" class="mb-4 px-6">
                  <BaseTip severity="secondary" class="items-center [&>svg]:translate-y-0">
                    <template #icon>
                      <EyeOff :stroke-width="2.5" />
                    </template>
                    <template #text>
                      {{
                        activeAppTab === 'loyalty'
                          ? t('pages.loyaltySupports.webapp.editor.panel.loyaltyHidden')
                          : t('pages.loyaltySupports.webapp.editor.screens.hidden')
                      }}
                    </template>
                    <template #button>
                      <Button variant="outline" size="sm" @click="showScreen(activeAppTab)">
                        {{ t('pages.loyaltySupports.webapp.editor.screens.show') }}
                      </Button>
                    </template>
                  </BaseTip>
                </div>

                <div
                  v-if="!openedSection && activeScreenSection && isPreviewOnlySection(activeScreenSection)"
                  class="mb-4 px-6"
                >
                  <PreviewOnlyBadge />
                </div>

                <RegistrationStepList
                  v-if="activeScreenSection === REGISTRATION_SECTION.id"
                  :has-form-completion="webapp.hasFormCompletion"
                  @open="openSection"
                />

                <OrderChannelList v-else-if="activeScreenSection === SCREEN_SECTIONS.order.id" @open="openSection" />

                <template v-else-if="activeScreenSection">
                  <Tabs v-if="hasTextTab(activeScreenSection) && !contentEditing" v-model="blockTab" class="mb-6 px-6">
                    <TabsList class="w-full">
                      <TabsTrigger value="content" class="flex-1">
                        {{ t('pages.loyaltySupports.webapp.editor.panel.content') }}
                      </TabsTrigger>
                      <TabsTrigger value="text" class="flex-1">{{ textTabLabel(activeScreenSection) }}</TabsTrigger>
                    </TabsList>
                  </Tabs>

                  <Transition
                    mode="out-in"
                    enter-active-class="motion-safe:animate-panel-switch"
                    leave-active-class="motion-safe:animate-panel-out"
                  >
                    <SectionPanel
                      :key="hasTextTab(activeScreenSection) ? blockTab : 'content'"
                      v-model:images="webappImages"
                      v-model:deleted-images="deletedImages"
                      :section="activeScreenSection"
                      :loyalty-stores="loyaltyStores"
                      :initial-images="webapp.images"
                      :tab="hasTextTab(activeScreenSection) ? blockTab : undefined"
                    />
                  </Transition>
                </template>

                <template v-else>
                  <SectionList
                    :sections="screenSections"
                    :toggle-ids="toggleableHomeBlocks"
                    :enabled-ids="brandAppConfig.home.value.blocks"
                    :coming-soon-ids="activeAppTab === 'home' ? COMING_SOON_HOME_BLOCKS : []"
                    @open="openSection"
                    @toggle="setHomeBlockEnabled"
                  />
                </template>
              </template>
            </div>
          </Transition>
        </div>
        <div id="brand-app-editor-footer" />
      </div>

      <div class="relative min-h-0 min-w-0 flex-[6]">
        <div
          v-if="mode === 'screen' && openedSection === 'reg-profile' && !webapp.hasFormCompletion"
          class="pointer-events-none absolute inset-x-0 top-[calc(85%+1rem)] z-10 flex justify-center"
        >
          <BaseChip :text="t('pages.loyaltySupports.webapp.editor.panel.profileSkipped')" color="gray" />
        </div>
        <div
          v-if="mode === 'screen' && contentDraft"
          class="absolute inset-x-0 top-[calc(85%+1rem)] z-10 flex flex-col items-center gap-2"
        >
          <Tabs v-if="contentDraft.kind !== 'mission'" v-model="contentView">
            <TabsList>
              <TabsTrigger value="card">{{ t('pages.loyaltySupports.webapp.news.card') }}</TabsTrigger>
              <TabsTrigger value="detail">{{ t('pages.loyaltySupports.webapp.news.detail') }}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <PreviewTab
          :base-url="webappURL"
          :cgu-url="loyaltyProgram.cguUrl"
          :cgu-wording="loyaltyProgram.cguWording"
          :privacy-policy-url="loyaltyProgram.privacyPolicyUrl"
          :checkin-points="Number(merchantConfigs?.checkinPoints)"
          :has-points-by-currency="merchantConfigs?.hasPointsByCurrency"
          :loyalty-points-by-currency-unit="merchantConfigs?.loyaltyPointsByCurrencyUnit"
          :has-form-completion="webapp.hasFormCompletion"
          :preview-status-name="previewStatusName"
          :images="renderImages"
          :logo="brandAppConfig.identity.value.logo"
          :merchant="user.merchant"
          :points-label="merchantTexts?.pointsLabel"
          :primary-color="brandAppConfig.identity.value.primary"
          :registration-texts="registrationTexts"
          :rewards="rewards"
          :slug="user.merchant.slug"
          :store-name="firstStoreName"
          :subtitle="welcomeTexts.phoneTitle"
          :title="welcomeTexts.title"
          :preview-zone="isColorPreviewed && activeAppTab === 'loyalty' ? 'home' : previewZone"
          :is-color-previewed="isColorPreviewed"
          :home-section="mode === 'screen' && activeAppTab === 'home' ? openedSection : null"
          :use-polite-language="brandAppConfig.identity.value.usePoliteLanguage"
          @tab-clicked="onPreviewTabClicked"
          @block-clicked="onPreviewBlockClicked"
        />
      </div>
    </div>

    <BaseModal :is-open="isUnsavedChangesModalOpen" size="lg" :show-footer="false" @close="cancelLeave">
      <div class="flex w-full flex-row justify-center">
        <div class="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
          <TriangleAlert class="h-6 w-6 text-amber-600" />
        </div>
      </div>
      <p class="mt-5 text-center font-semibold">
        {{ t('pages.loyaltySupports.webapp.unsavedChanges.title') }}
      </p>
      <p class="text-foreground-secondary mt-2 text-center text-sm">
        {{ t('pages.loyaltySupports.webapp.unsavedChanges.body') }}
      </p>
      <div class="mt-6 flex w-full flex-row justify-center gap-3">
        <Button variant="outline" class="flex-1" @click="cancelLeave">
          {{ t('global.words.cancel') }}
        </Button>
        <Button variant="default-primary" class="flex-1" @click="confirmLeave">
          {{ t('pages.loyaltySupports.webapp.unsavedChanges.confirm') }}
        </Button>
      </div>
    </BaseModal>
  </div>
  <TheLoadingPage v-else />
</template>
